/**
 * PHP/Composer Dependencies Service
 */
import { execSync } from 'child_process';
import { promises as fs } from 'fs';
import path from 'path';
import type {
    DepsServiceInterface,
    PackageInfo,
    CheckResult,
    UpgradeOptions,
    UpgradeType
} from '../types/deps.types.js';

interface ComposerOutdatedPackage {
    name: string;
    version: string;
    latest: string;
    'latest-status': string;
    description?: string;
}

interface ComposerOutdatedResult {
    installed: ComposerOutdatedPackage[];
}

interface ComposerJson {
    name?: string;
    require?: Record<string, string>;
    'require-dev'?: Record<string, string>;
}

const LARAVEL_PROTECTED_PACKAGES = /^laravel\//;

export class DepsPhpService implements DepsServiceInterface {
    readonly ecosystem = 'php' as const;
    private isLaravel = false;

    async check(cwd: string, onProgress?: (completed: number, total: number) => void): Promise<CheckResult> {
        this.isLaravel = await this.detectLaravel(cwd);
        const outdated = await this.fetchOutdatedPackages(cwd);
        const composerJson = await this.readComposerJson(cwd);

        const packages: PackageInfo[] = [];
        let processed = 0;

        for (const pkg of outdated) {
            const isDev = pkg.name in (composerJson['require-dev'] || {});
            const upgradeType = this.getUpgradeType(pkg.version, pkg.latest);
            const canUpgrade = this.canUpgradePackage(pkg.name, upgradeType);
            const blockedReason = !canUpgrade ? 'Laravel major version blocked' : undefined;

            packages.push({
                name: pkg.name,
                current: pkg.version,
                latest: pkg.latest,
                latestWithPrefix: pkg.latest,
                type: isDev ? 'dev' : 'dep',
                upgradeType,
                canUpgrade,
                blockedReason
            });

            processed++;
            onProgress?.(processed, outdated.length);
        }

        return {
            ecosystem: this.ecosystem,
            manager: 'composer',
            packages: packages.filter(p => p.canUpgrade),
            isLaravel: this.isLaravel
        };
    }

    async upgrade(cwd: string, options: UpgradeOptions): Promise<void> {
        const commands = this.getUpgradeCommands(options.packages);
        if (commands.deps) this.executeUpgrade(commands.deps, cwd);
        if (commands.devDeps) this.executeUpgrade(commands.devDeps, cwd);
    }

    getUpgradeCommands(packages: PackageInfo[]): { deps: string | null; devDeps: string | null } {
        const deps = packages.filter(p => p.type === 'dep');
        const devDeps = packages.filter(p => p.type === 'dev');

        let depsCmd: string | null = null;
        let devDepsCmd: string | null = null;

        if (deps.length > 0) {
            const pkgList = deps.map(p => `${p.name}:${p.latest}`).join(' ');
            depsCmd = `composer require -W ${pkgList}`;
        }

        if (devDeps.length > 0) {
            const pkgList = devDeps.map(p => `${p.name}:${p.latest}`).join(' ');
            devDepsCmd = `composer require --dev -W ${pkgList}`;
        }

        return { deps: depsCmd, devDeps: devDepsCmd };
    }

    private async readComposerJson(cwd: string): Promise<ComposerJson> {
        const composerPath = path.join(cwd, 'composer.json');
        try {
            const content = await fs.readFile(composerPath, 'utf-8');
            return JSON.parse(content) as ComposerJson;
        } catch (error) {
            if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
                throw new Error('Không tìm thấy composer.json trong thư mục hiện tại');
            }
            throw new Error(`Lỗi đọc composer.json: ${(error as Error).message}`);
        }
    }

    private async detectLaravel(cwd: string): Promise<boolean> {
        try {
            const composerJson = await this.readComposerJson(cwd);
            const deps = { ...composerJson.require, ...composerJson['require-dev'] };
            return 'laravel/framework' in deps;
        } catch {
            return false;
        }
    }

    private async fetchOutdatedPackages(cwd: string): Promise<ComposerOutdatedPackage[]> {
        try {
            const output = execSync('composer outdated --format=json --direct', {
                cwd,
                encoding: 'utf-8',
                stdio: ['pipe', 'pipe', 'pipe']
            });
            const result = JSON.parse(output) as ComposerOutdatedResult;
            return result.installed || [];
        } catch (error) {
            if ((error as any).stdout) {
                try {
                    const result = JSON.parse((error as any).stdout) as ComposerOutdatedResult;
                    return result.installed || [];
                } catch {}
            }
            throw new Error(`Lỗi kiểm tra composer packages: ${(error as Error).message}`);
        }
    }

    private getUpgradeType(current: string, latest: string): UpgradeType {
        const currentParts = current.split('.').map(Number);
        const latestParts = latest.split('.').map(Number);
        if ((latestParts[0] || 0) > (currentParts[0] || 0)) return 'major';
        if ((latestParts[1] || 0) > (currentParts[1] || 0)) return 'minor';
        return 'patch';
    }

    private canUpgradePackage(packageName: string, upgradeType: UpgradeType): boolean {
        if (this.isLaravel && LARAVEL_PROTECTED_PACKAGES.test(packageName) && upgradeType === 'major') {
            return false;
        }
        return true;
    }

    private executeUpgrade(command: string, cwd: string): void {
        execSync(command, {
            cwd,
            stdio: 'inherit',
            env: { ...process.env, FORCE_COLOR: '1' }
        });
    }
}
